import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to manage Cmd+K / Ctrl+K keyboard shortcut for opening the command palette.
 * Also handles closing the dialog on route changes.
 */
export function useCmdKShortcut() {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  // Global keyboard listener for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        const target = e.target as HTMLElement;
        // Don't trigger if user is typing in an input/textarea/contenteditable
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dialog when route changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: router.asPath triggers effect on navigation
  useEffect(() => {
    setOpen(false);
  }, [router.asPath]);

  const onOpenChange = useCallback((value: boolean) => {
    setOpen(value);
  }, []);

  return { open, onOpenChange };
}
